import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/auth/AuthContext";
import type { WSEvent, WSEventType } from "./types";

/** Singleton-per-mount WebSocket lifecycle.
 *
 *  The provider opens a single connection to ``/api/ws`` once the user is
 *  authenticated, automatically reconnects with capped exponential backoff
 *  on disconnect, and pings every 30s to keep proxies from killing idle
 *  TCP. Consumers subscribe via ``useWS().on(type, handler)`` and get a
 *  cleanup function back — the same shape as document.addEventListener.
 *
 *  We intentionally don't expose ``send`` here: REST is the source of
 *  truth for state mutations (messages / read-cursors / etc.), and the
 *  WS is a one-way push channel for UI sync. The only client → server
 *  message we ever send is a ``"ping"`` keepalive, owned internally.
 */
type Handler<T extends WSEventType = WSEventType> = (
  ev: Extract<WSEvent, { type: T }>,
) => void;

interface WSContextShape {
  /** True iff the socket is currently OPEN. */
  connected: boolean;
  /**
   * Subscribe to one event type. Returns an unsubscribe function — call
   * it from useEffect cleanup. Multiple components can subscribe to the
   * same event independently.
   */
  on: <T extends WSEventType>(type: T, handler: Handler<T>) => () => void;
  /**
   * Send a JSON envelope to the server. Currently used for transient
   * typing-indicator pings; state mutations still go through REST.
   * Drops silently when the socket isn't open — the cost of a missed
   * "X is typing…" frame is zero.
   */
  send: (payload: Record<string, unknown>) => void;
}

const WSContext = createContext<WSContextShape | null>(null);

const PING_INTERVAL_MS = 30_000;
// How often, at most, we tell the server "the human is actually here" while
// they keep interacting. This is DISTINCT from the keepalive ping: the ping
// proves the socket is alive; this proves a person is driving it. Throttled so
// continuous mouse movement is at most one small frame every 45s.
const ACTIVE_HEARTBEAT_MS = 45_000;
// Reconnect backoff: 0.5s, 1s, 2s, 4s, capped at 10s. Reset on a
// successful open so a recovery from a long disconnect doesn't leave
// us at the cap forever.
const BACKOFF_MIN_MS = 500;
const BACKOFF_MAX_MS = 10_000;

function wsUrl(): string {
  // Match the page's protocol so the cookie travels and HTTPS stays
  // HTTPS. Same origin so the session cookie automatically tags along.
  if (typeof window === "undefined") return "";
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/ws`;
}

export function WSProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  // Subscribers per event type. We don't reuse a single map because
  // adding a handler shouldn't be O(n in all subscribers).
  const subsRef = useRef<Map<WSEventType, Set<Handler>>>(new Map());
  const backoffRef = useRef(BACKOFF_MIN_MS);
  const reconnectTimerRef = useRef<number | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  // Last time we sent an ``active`` frame — throttles the activity heartbeat.
  const lastActiveSentRef = useRef(0);
  // Ref for the user so the (re)connect loop doesn't capture a stale
  // closure when the user logs out + back in mid-session.
  const userRef = useRef(user);
  userRef.current = user;

  const dispatch = useCallback((evt: WSEvent) => {
    const handlers = subsRef.current.get(evt.type);
    if (!handlers) return;
    for (const h of handlers) {
      try {
        // The narrow type-cast is safe — handlers are stored under
        // their event type, so the union has been already disambiguated.
        (h as Handler)(evt);
      } catch (err) {
        // A bad handler must not break the dispatch loop; surface to
        // console so the bug is debuggable.
        // eslint-disable-next-line no-console
        console.error("WS handler threw", err);
      }
    }
  }, []);

  // True only when this tab is the one the human is actually looking at:
  // visible AND the OS window focused. Presence must not count a backgrounded
  // tab or an unfocused window (another app / another monitor) as "active".
  const isFocused = () =>
    typeof document !== "undefined" &&
    !document.hidden &&
    (typeof document.hasFocus !== "function" || document.hasFocus());

  const sendIdle = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "idle" }));
    } catch {
      // ignore — the server ages us to away/offline anyway.
    }
  }, []);

  // Report real user interaction to the server, throttled. ``force`` bypasses
  // the throttle (used on refocus so presence flips to active immediately).
  // Gated on focus so a background/unfocused tab — or a socket that merely
  // reconnected on an unattended machine — never reports itself active. A
  // background keepalive ping never calls this either: only genuine
  // mouse/keyboard/scroll on the focused tab does. That's what keeps presence
  // honest for a tab left open on another computer.
  const reportActive = useCallback((force = false) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (!isFocused()) return;
    const now = Date.now();
    if (!force && now - lastActiveSentRef.current < ACTIVE_HEARTBEAT_MS) return;
    lastActiveSentRef.current = now;
    try {
      ws.send(JSON.stringify({ type: "active" }));
    } catch {
      // Socket broke between the check and the send — the close handler
      // will reconnect; a dropped activity frame is harmless.
    }
  }, []);

  const teardown = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current !== null) {
      window.clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!userRef.current) return;
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) return;

    const ws = new WebSocket(wsUrl());
    wsRef.current = ws;

    ws.addEventListener("open", () => {
      backoffRef.current = BACKOFF_MIN_MS;
      setConnected(true);
      // Report the tab's real state on (re)connect. Only a focused, visible
      // tab counts as a human arrival → active. A reconnect on a backgrounded
      // or unattended tab reports idle instead, so a network blip / server
      // restart can never re-light someone as "active" without them being
      // there — the phantom-active bug on forgotten tabs.
      if (isFocused()) {
        lastActiveSentRef.current = Date.now();
        try {
          ws.send(JSON.stringify({ type: "active" }));
        } catch {
          // ignore — interaction listeners will report on the next move.
        }
      } else {
        try {
          ws.send(JSON.stringify({ type: "idle" }));
        } catch {
          // ignore — the server ages us to away/offline anyway.
        }
      }
      // Periodic keepalive — server replies with ``{type:"pong"}``,
      // dispatched but otherwise ignored.
      if (pingTimerRef.current !== null) {
        window.clearInterval(pingTimerRef.current);
      }
      pingTimerRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send("ping");
      }, PING_INTERVAL_MS);
    });

    ws.addEventListener("message", (ev) => {
      try {
        const data = JSON.parse(ev.data) as WSEvent;
        dispatch(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("WS bad payload", err);
      }
    });

    const scheduleReconnect = () => {
      // Authentication failures (4401 / 4403) shouldn't keep retrying —
      // there's no path to recovery without a fresh login. Other close
      // codes (network drop, server restart) get the backoff treatment.
      if (ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) return;
      teardown();
      if (!userRef.current) return;
      const delay = backoffRef.current;
      backoffRef.current = Math.min(BACKOFF_MAX_MS, backoffRef.current * 2);
      reconnectTimerRef.current = window.setTimeout(connect, delay);
    };

    ws.addEventListener("close", (ev) => {
      // 4401 / 4403 — auth failed; the AuthProvider will eventually
      // redirect to /login. Don't reconnect on those.
      if (ev.code === 4401 || ev.code === 4403) {
        teardown();
        return;
      }
      scheduleReconnect();
    });
    ws.addEventListener("error", () => {
      // ``error`` is always followed by ``close``; let the close
      // handler own the reconnect schedule.
    });
  }, [dispatch, teardown]);

  // Open / close on auth changes.
  useEffect(() => {
    if (!user) {
      teardown();
      return;
    }
    connect();
    return teardown;
  }, [user, connect, teardown]);

  // Presence: report real interaction (throttled) and drop to "idle" the
  // moment the tab is hidden, so an idle / backgrounded tab reads as *away*
  // instead of masquerading as active. Only mounted while logged in.
  useEffect(() => {
    if (!user) return;
    const onInteract = () => reportActive();
    // Leaving the tab/app → idle immediately; coming back → active. We cover
    // both "switched browser tab" (visibilitychange) and "switched to another
    // app / monitor" (window blur/focus), so an unfocused-but-visible tab
    // doesn't linger as active.
    const onVisibility = () => {
      if (document.hidden) sendIdle();
      else reportActive(true);
    };
    const onBlur = () => sendIdle();
    const onFocus = () => reportActive(true);
    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener("pointerdown", onInteract, opts);
    window.addEventListener("keydown", onInteract, opts);
    window.addEventListener("mousemove", onInteract, opts);
    window.addEventListener("scroll", onInteract, opts);
    window.addEventListener("touchstart", onInteract, opts);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("mousemove", onInteract);
      window.removeEventListener("scroll", onInteract);
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, reportActive, sendIdle]);

  const on = useCallback<WSContextShape["on"]>((type, handler) => {
    let bucket = subsRef.current.get(type);
    if (!bucket) {
      bucket = new Set();
      subsRef.current.set(type, bucket);
    }
    // Cast through ``unknown``: ``Handler<T>`` and the bucket's default
    // ``Handler<WSEventType>`` are different specializations of the same
    // generic and TS won't bridge them directly, but the runtime contract
    // (a function the dispatcher invokes with the matching event) is
    // preserved by the ``type`` key the bucket is stored under.
    bucket.add(handler as unknown as Handler);
    return () => {
      const b = subsRef.current.get(type);
      if (b) {
        b.delete(handler as unknown as Handler);
        if (b.size === 0) subsRef.current.delete(type);
      }
    };
  }, []);

  const send = useCallback((payload: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(payload));
    } catch {
      // Connection broke right between the readyState check and the
      // send — ignore; the close handler will reconnect.
    }
  }, []);

  return (
    <WSContext.Provider value={{ connected, on, send }}>
      {children}
    </WSContext.Provider>
  );
}

export function useWS(): WSContextShape {
  const ctx = useContext(WSContext);
  if (!ctx) {
    // Falling back to a no-op shape lets components render in tests /
    // when the provider hasn't mounted yet without throwing.
    return {
      connected: false,
      on: () => () => undefined,
      send: () => undefined,
    };
  }
  return ctx;
}

/**
 * Convenience hook: subscribe to a single WS event with stable handler
 * semantics (the latest closure is always called, no stale captures).
 */
export function useWSEvent<T extends WSEventType>(
  type: T,
  handler: (ev: Extract<WSEvent, { type: T }>) => void,
) {
  const { on } = useWS();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    return on(type, (ev) => ref.current(ev));
  }, [on, type]);
}
